/**
 * The two controls the field-mapping screens share: what a field is for, and
 * which statistic it is.
 *
 * Both screens classify the same fields against the same Stat Dictionary, and
 * both declarations of how used to be written out twice. They drifted: `AddSource`
 * never grew the `carried` option, so every unrecognised number — most of a
 * real source — rendered as *Ignore*, which is its opposite. A `<select>` whose
 * value matches none of its options shows the first one, and the first one was
 * Ignore. The screen reported kept data as deleted, and it typechecked.
 *
 * So the role list and the Statistic cell live here, once. Filtering does not:
 * `AddSource` fetches one sport's dictionary and the review screen fetches all
 * of it, and folding that choice in here would make one screen's scope decision
 * the other's.
 */

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  api,
  type Direction,
  type RepeatedIdentity,
  type Role,
  type StatEntry,
} from "../api";
import { keys } from "../queries";
import { blockForRole, parseOrder } from "../column-order";
import { REPEATED_IDENTITY_OPTIONS, repeatedIdentityHint } from "../repeated-identity";
import { statOptions } from "../stat-search";
import { isPendingWeight, parseWeightPercent, weightOnStatChange } from "../stat-weight";
import { exclusionHint, labelsFor, optionsFor } from "../position-exclusions";
import { PositionGroupPicker } from "./PositionGroupPicker";
import type { DraftSource } from "./AddPositionGroup";
import { StatisticMenu } from "./StatisticMenu";
import { AlertIcon, InfoIcon } from "./icons";
import {
  Banner,
  Button,
  Field,
  FieldSet,
  Muted,
  Select,
  SelectCompact,
  TextInputCompact,
  Tooltip,
} from "./ui";

/**
 * What a repeated Identity Key means, as one control both screens render.
 *
 * It sits beside the role list for the same reason that does: it is a decision
 * about telling players apart, it is made while the fields are being reviewed,
 * and written out twice it would drift. It is a *profile* answer rather than a
 * per-field one — there is one Identity Key — so it renders above the table
 * rather than in it.
 *
 * **Why it needs a control at all.** The value has existed since the parser
 * did, and only a Source Template could set it: a publisher that turned out to
 * repeat rows after its template shipped could be fixed by nothing on any
 * screen. Measured on InStat's NCAA D1 Men feed, which repeats 18 of 1,597 rows
 * byte-for-byte — six players, every copy identical — and failed every Run with
 * `identity_collision` until this was set.
 *
 * The options and the hints are in `../repeated-identity`, which has no React
 * in it and so can be tested.
 */
export function RepeatedIdentityField({
  value,
  onChange,
}: {
  value: RepeatedIdentity;
  onChange: (value: RepeatedIdentity) => void;
}) {
  return (
    <Field
      label="When one Identity Key appears twice in a Feed"
      hint={repeatedIdentityHint(value)}
    >
      <Select value={value} onChange={(e) => onChange(e.target.value as RepeatedIdentity)}>
        {REPEATED_IDENTITY_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </Field>
  );
}

/**
 * What a field can be for. One declaration, rendered by both screens.
 *
 * Order is screen order and it is deliberate: *Exclude from ranks* sits directly
 * after *Ignore* because they are the two answers that need no statistic, and
 * putting the opposites side by side is what makes choosing between them a
 * decision rather than an accident.
 */
export const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: "ignored", label: "Ignore" },
  // Carried fields reach the raw file untouched and never reach the ranker, so
  // they need no statistic and no Direction. That is what keeps reviewing a
  // thirteen-report source proportional to the ranking rather than to the
  // payload: classify the handful of columns that score a player and carry the
  // rest. It is also the default for a number nobody recognises.
  { value: "carried", label: "Exclude from ranks" },
  { value: "player_name", label: "Player name" },
  { value: "team", label: "Team" },
  { value: "position", label: "Position" },
  // Beside Position because it is the same kind of answer — a fact about the
  // player rather than a number about his season — and because that is where a
  // reviewer scanning for biodata will look for it. It publishes as its own
  // `Draft Season` column and never reaches the ranker, so like Team and
  // Position it needs no statistic and no Direction.
  { value: "draft_season", label: "Draft season" },
  { value: "stat", label: "Statistic" },
  { value: "identity", label: "Identity Key" },
];

/**
 * The key a display name is filed under.
 *
 * Derived rather than typed, and shown rather than editable, because `key` is
 * the one column the API cannot patch — `StatEntryPatch` exposes no `key` — and
 * `Binding.stat_key` is a plain string rather than a foreign key. Deleting an
 * entry to correct a typo silently dangles every Binding pointing at it, so a
 * free text field over the one immutable, un-cascaded column is backwards
 * (ADR-0029). Snake case, which is what every Vocabulary entry already uses.
 *
 * Capped at the column's 120 characters, and the trailing separator a cut can
 * leave behind is taken off after the cut rather than before it.
 */
export function statKeyFrom(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 120)
    .replace(/_+$/g, "");
}

/**
 * The Statistic cell: which entry this field is, and a way out when it is none
 * of them.
 *
 * `+ New` is always visible rather than appearing when the dropdown comes up
 * empty. A reviewer who has to discover that the dictionary is missing the
 * statistic *before* the control to add it exists has already met the dead end
 * the control is for.
 *
 * **Choosing a statistic can fill in the row's Weight**, because the dictionary
 * entry carries one and every other way of reaching a statistic already copies
 * it. The rule — and what it refuses to overwrite — is `weightOnStatChange`.
 *
 * **A stored value is always offered, even when `entries` does not hold it.**
 * Issue 01 was a `<select>` whose value matched none of its options, which
 * renders as the first option instead — the control saying *Ignore* over a
 * field it was carrying. Scoping the dropdown to one sport (issue 03)
 * reintroduces exactly that shape for a Binding saved before the scope
 * existed: filter the options and the stored `stat_key` stops being among
 * them, so the cell would read "Choose a statistic…" over a field that is
 * bound. The value is added back as its own option instead. Silently blanking
 * it would be the same lie in the other direction, and re-selecting a value
 * the reviewer never cleared is not the screen's decision to make.
 *
 * Which row that is, and what it is called, is `statOptions` — a pure function,
 * so the rule survives as a test rather than as a paragraph. The control itself
 * is a searchable listbox rather than a `<select>`, for the length of the list:
 * see `StatisticMenu`.
 */
export function StatisticCell({
  value,
  weight,
  entries,
  outOfSport = null,
  label,
  onChange,
  onNew,
  newOpen,
  groupLabels,
}: {
  value: string | null;
  /**
   * The row's declared Weight, which choosing a statistic may fill in.
   *
   * Passed down rather than read back up because the decision needs both halves
   * at once — see `weightOnStatChange`, which holds the rule — and because the
   * two screens keep their rows in different shapes. The cell reads it and
   * returns it; it stores neither.
   */
  weight: number | null;
  /** What this profile's sport offers. Scoped by the caller, not here. */
  entries: StatEntry[];
  /**
   * The entry a stored `stat_key` resolves to in some *other* sport, when this
   * profile's sport has none under that key. Supplied only to name the option
   * honestly — a bare key reads as a typo where "Touchdowns — football" reads
   * as what it is.
   */
  outOfSport?: StatEntry | null;
  /** What a screen reader calls the box, since the column header speaks for
   *  every row at once. Both screens name the field. */
  label?: string;
  /**
   * The chosen statistic and the row's Weight after the choice, which is the
   * Weight unchanged on every row where somebody has already declared one.
   */
  onChange: (statKey: string | null, weight: number | null) => void;
  onNew: () => void;
  /** Whether this row's form is the open one — announced, not styled. */
  newOpen: boolean;
  /**
   * What feeds each of the sport's Canonical Position Groups.
   *
   * Passed down rather than queried here for the reason `entries` is: this
   * renders once per row, and a table of 488 would hold 488 subscriptions to
   * one cache key. The screens fetch it once and hand it in.
   */
  groupLabels?: Readonly<Record<string, readonly string[]>>;
}) {
  const options = useMemo(
    () => statOptions(entries, value, outOfSport, "rank", groupLabels),
    [entries, value, outOfSport, groupLabels],
  );

  /**
   * What the dictionary says a statistic is worth, across the same list the
   * menu offers — the out-of-sport entry included, since it is an option here
   * and the weight on it is as declared as any other.
   *
   * A key the list does not hold reads as null, which is what an entry deleted
   * out from under a bound row leaves behind: no default, so nothing to seed
   * from and nothing recognised as seeded.
   */
  const defaultWeightOf = (statKey: string | null): number | null => {
    if (statKey === null) return null;
    if (outOfSport && outOfSport.key === statKey) return outOfSport.default_weight;
    return entries.find((entry) => entry.key === statKey)?.default_weight ?? null;
  };

  return (
    <div className="flex items-center gap-xs">
      <StatisticMenu
        value={value}
        options={options}
        label={label}
        onChange={(statKey) =>
          onChange(
            statKey,
            weightOnStatChange({
              weight,
              previousDefault: defaultWeightOf(value),
              nextDefault: defaultWeightOf(statKey),
            }),
          )
        }
      />
      {/* A "+" rather than "+ New": the words cost 45px of a cell that then
          truncated the statistic itself to one letter — "F" where it meant
          "Field Goals Made". The name it used to carry is on the title and the
          accessible name, so nothing is lost but the width. */}
      <Button
        size="sm"
        onClick={onNew}
        title="Add a statistic the dictionary does not have yet"
        aria-label="Add a statistic the dictionary does not have yet"
        aria-expanded={newOpen}
      >
        +
      </Button>
    </div>
  );
}

/**
 * One scored field's Weight.
 *
 * Shared by Add Source and the Fields screen, which hold their rows in
 * different shapes — a `Record` keyed by path and a list keyed by Feed and path
 * — so the cell takes the weight and a callback and knows about neither.
 *
 * **The per-row share is no longer printed under the box.** It was there
 * because the box is called a percentage and a relative weight means nothing
 * until the reader knows the total — a real argument, and it lost to the cost
 * of running it under every scored row at once: a column of fields each
 * carrying two stacked lines of grey micro-type, on a thirteen-report source
 * where that is a hundred-odd rows. The arithmetic has not changed and is not
 * hidden: `weightNotice` still speaks for the set of them above the table, and
 * `sharesOf` still computes the ranker's own figure for anything that wants it.
 *
 * **The box holds text, not a number.** Parsing each keystroke into a number
 * would fight the cursor on a half-typed value. A value the parser refuses is
 * shown with its reason and **not** pushed up, so the row keeps the last weight
 * it accepted — the refusal is the signal that the number on screen is not the
 * number that will save.
 *
 * **A refusal that is only a prefix waits for the box to be left.** "0.5" is
 * typed as "0", then "0.", and `parseWeightPercent` refuses both — correctly,
 * since each is nought. Surfacing that on the keystroke would flash an error
 * twice on the way to a value the cell was going to take, so `isPendingWeight`
 * holds those back until blur, and somebody who really did type "0" and walk
 * away still finds out.
 */
/**
 * One sample value, capped so a long one cannot widen the table.
 *
 * A sample is evidence at a glance — is this a grade, a count, a name? — and
 * a publisher that answers with a sentence should not push the Statistic
 * column off the screen to prove it. The whole value is on the `title`.
 *
 * **8rem, raised from 3rem.** 48px fitted about five characters, which turned
 * every piece of evidence into an ellipsis: `Arge…` for a citizenship, `Jay F…`
 * for a headshot's alt text, `coun…` for a flag's rel. A reviewer deciding what
 * a field *is* could not read any of them. The cap still exists, and still
 * stops a sentence widening the table — it is a cap on a glance, not on the
 * value, and the whole value stays on the `title`.
 */
export function SampleCell({ value }: { value: string }) {
  // `inline-block`, not a block: a block fills its cell, so `max-w` became a
  // *minimum* and widened the Sample column from 82px to 124px — the opposite
  // of the point. Shrink-to-fit up to the cap is what was wanted.
  return (
    <span
      className="inline-block max-w-[8rem] truncate align-middle text-body-sm text-muted"
      title={value}
    >
      {value}
    </span>
  );
}

export function WeightCell({
  path,
  weight,
  onChange,
}: {
  path: string;
  weight: number | null;
  onChange: (weight: number | null) => void;
}) {
  const [text, setText] = useState(weight == null ? "" : String(weight));
  /** Whether the box has been left since a prefix-shaped refusal was typed. */
  const [left, setLeft] = useState(false);
  const parsed = parseWeightPercent(text);
  const shown = parsed.ok ? parsed.weight : null;
  // A refusal worth showing: anything that is not a prefix of a valid weight,
  // and a prefix only once the reader has moved on from the box. Written as
  // nested ternaries rather than one `||` so `parsed.ok` narrows the union and
  // `parsed.message` needs no cast to reach.
  const refusal = parsed.ok ? null : isPendingWeight(text) && !left ? null : parsed.message;
  // Accepted but worth a word — a weight above 100, which the API stores and a
  // percentage has no room for.
  const warning = parsed.ok ? parsed.warning : undefined;
  /**
   * What a refusal costs, said out loud.
   *
   * A refused weight is not pushed up, and neither screen gates its Save on one,
   * so the row saves the last value this cell accepted while the box shows
   * something else. That disagreement was silent. Naming the number that will
   * actually be written is the cheap half of the fix — the expensive half,
   * blocking Save, means wiring a per-row error into two different error models
   * and is not what this change is.
   */
  const willSave = weight == null ? "nothing declared" : `${weight}`;

  /**
   * Re-seed when the row's weight changes from outside this cell.
   *
   * A Source Template composing a whole field review does exactly that — MLS
   * NEXT Pro arrives with 27 of them — as does re-reading a source. Guarded on
   * the value differing from what the box already says, so the state this cell
   * is itself producing never interrupts typing, and a refused entry is not
   * wiped by the last good value flowing back down.
   */
  useEffect(() => {
    if (weight !== shown) setText(weight == null ? "" : String(weight));
    // `shown` is deliberately not a dependency: this is about the weight
    // arriving from above, and including it would re-run on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weight]);

  return (
    <div className="flex flex-col gap-xxs">
      <TextInputCompact
        aria-label={`Weight percentage for ${path}`}
        width="w-[72px]"
        inputMode="decimal"
        placeholder="1"
        aria-invalid={refusal ? true : undefined}
        title={warning ?? undefined}
        value={text}
        onBlur={() => setLeft(true)}
        onChange={(e) => {
          setText(e.target.value);
          setLeft(false);
          const next = parseWeightPercent(e.target.value);
          if (next.ok) onChange(next.weight);
        }}
      />
      {/* Plain spans rather than `Muted`, which carries `text-body-sm`: two
          type utilities on one element resolve by stylesheet order rather than
          by which was written last, so layering `text-micro` on it would
          produce whichever size the sheet happens to declare second. The
          stylesheet says so itself, above `.code-sm`. */}
      {/* Only ever a refusal or a warning now, so at most one line sits under
          the box and only on a row that has something wrong with it. Both are
          about what this cell will save and neither is removable on those
          grounds: a refused value is not pushed up, and the weight above 100 a
          warning names is legal, already stored, and must stay editable. */}
      {refusal ? (
        <span className="text-micro text-danger-ink">
          {refusal} This row will save as {willSave}.
        </span>
      ) : (
        warning && <span className="text-micro text-warning-ink">{warning}</span>
      )}
    </div>
  );
}

/**
 * What the Order priority column means, said once for both screens.
 *
 * It is a **sorting value, not a seat number**, and almost every way people get
 * it wrong follows from reading it as a seat: ten fields set to 1 (a tie, not
 * ten first columns), 10000 against a file of thirty columns (legal — it just
 * means last), a blank read as nought rather than as "no preference". The
 * heading now says *priority*, and this says the rest.
 *
 * Carried by `Tooltip` rather than by a `title` attribute. `title` was the
 * first choice — it is how every other hint here is carried — and it was
 * measured at three to four seconds before appearing, which is a delay the
 * browser owns and nothing can shorten. A hint whose job is to be found cannot
 * arrive after the reader has given up looking.
 */
export const ORDER_HINT =
  "Lower numbers come first, so 1 leads the file. " +
  "The gaps do not matter: 1, 2, 3 and 10, 20, 30 publish the same order. " +
  "Leave it blank for no preference — blank columns follow every numbered one. " +
  "Two columns sharing a number fall back to alphabetical, so setting them all to 1 " +
  "is the same as leaving them all blank.";

/**
 * A column heading that carries an explanation.
 *
 * The icon is the affordance: a heading with no marker gives a reader no reason
 * to hover it, so the hint may as well not exist. `InfoIcon` rather than
 * `AlertIcon` deliberately — the triangle is this codebase's "something is
 * wrong" marker, on refusals and danger banners, and a healthy column wearing
 * one reads as an error.
 *
 * The `title` sits on the heading rather than on the icon so the whole cell is
 * the hover target, and `aria-label` carries the same words for a reader who
 * cannot hover at all.
 */
export function HintedHeading({ label, hint }: { label: string; hint: string }) {
  return (
    <Tooltip hint={hint} className="cursor-help">
      {label}
      <InfoIcon size={12} className="shrink-0 text-ink-subtle" aria-hidden="true" />
    </Tooltip>
  );
}

/**
 * Where this field's column sits in the published header.
 *
 * Here beside the Role and the Statistic for the reason this module exists at
 * all: both screens classify the same fields, and a control written out twice
 * drifts — Add Source never grew the `carried` option and rendered kept data as
 * deleted, and it typechecked. The Order column is the same kind of per-field
 * answer, so it is declared once.
 *
 * **A number rather than a drag handle**, which was the alternative and loses
 * on both of the cases that matter. The field tables are filtered by the queue
 * pills, so dragging inside a filtered view reorders a row against rows that
 * are not on screen; and a six-hundred-field source is not a list anybody
 * drags. A sparse integer survives both, and a field discovered by a later
 * re-read arrives undeclared and lands at the end rather than in the middle of
 * somebody's sequence. The up/down buttons beside the table are the sugar on
 * top, and they write this same number.
 *
 * **Shown on every row, not only the orderable ones.** A reviewer moving a
 * field between Statistic and *Exclude from ranks* is moving it between the two
 * blocks that publish columns, and a cell that vanished on the way would lose
 * the placement in the gap. Where a role publishes no column the box says so
 * instead of pretending the number does something — see the placeholder.
 *
 * The box holds text for the same reason the Weight box does: parsing every
 * keystroke into a number fights the cursor, and a value the parser refuses is
 * shown with its reason rather than pushed up, so the row keeps the last
 * position it accepted.
 */
export function OrderCell({
  path,
  sortOrder,
  role,
  onChange,
}: {
  path: string;
  sortOrder: number | null;
  role: Role;
  onChange: (sortOrder: number | null) => void;
}) {
  const [text, setText] = useState(sortOrder == null ? "" : String(sortOrder));
  const parsed = parseOrder(text);
  const shown = parsed.ok ? parsed.sortOrder : null;
  const refusal = parsed.ok ? null : parsed.message;
  // Which block this row's column lands in — and `null` for the roles that
  // publish no column of their own, where a position would be a number that
  // does nothing.
  const block = blockForRole(role);

  /**
   * Re-seed when the position arrives from outside this cell.
   *
   * Which is every press of Renumber and of the up/down buttons, as well as a
   * Source Template composing a whole review. Guarded on the value differing
   * from what the box already says, so the state this cell is itself producing
   * never interrupts typing.
   */
  useEffect(() => {
    if (sortOrder !== shown) setText(sortOrder == null ? "" : String(sortOrder));
    // `shown` is deliberately not a dependency: this is about the position
    // arriving from above, and including it would re-run on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOrder]);

  return (
    <div className="flex flex-col gap-xxs">
      <TextInputCompact
        aria-label={`Order priority for ${path}`}
        width="w-[64px]"
        inputMode="numeric"
        // **Not "1", which the Weight box can use** because an undeclared
        // weight really does rank as 1. An undeclared position does not sort
        // first, it sorts last, so a "1" here would say the opposite of what
        // the blank means. "last" is what it means and what it says.
        //
        // A role that publishes no column gets a dash instead: there is no
        // position for it to be last among, and the `title` says why.
        placeholder={block === null ? "—" : "last"}
        disabled={block === null}
        aria-invalid={refusal ? true : undefined}
        title={
          block === null
            ? "This Field Role publishes no column of its own, so it has no position to set."
            : undefined
        }
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          const next = parseOrder(e.target.value);
          if (next.ok) onChange(next.sortOrder);
        }}
      />
      {refusal && (
        <span className="text-micro text-danger-ink">
          {refusal} This row will save as{" "}
          {sortOrder == null ? "nothing declared" : `position ${sortOrder}`}.
        </span>
      )}
    </div>
  );
}

/**
 * Move one row up or down the header, without typing a number.
 *
 * One row rather than one field, because a row is a Binding and a Binding is
 * what carries a position: nudging one report of a split field is not a
 * statement about its siblings.
 *
 * Writes the same `sort_order` the box does — `moveRow` renumbers the block
 * first — because a position held only as the row's place in an array does not
 * survive a save: both reads of a profile's Bindings come back in the server's
 * order.
 *
 * Disabled at the ends of the block rather than hidden, so the column does not
 * change width on the first and last rows.
 */
export function OrderNudge({
  path,
  disabled,
  onMove,
}: {
  path: string;
  disabled: { up: boolean; down: boolean };
  onMove: (direction: "up" | "down") => void;
}) {
  return (
    <span className="inline-flex flex-col leading-none">
      <button
        type="button"
        aria-label={`Move ${path} earlier in the header`}
        disabled={disabled.up}
        onClick={() => onMove("up")}
        className="px-xxs text-micro text-muted hover:text-ink disabled:opacity-30"
      >
        ▲
      </button>
      <button
        type="button"
        aria-label={`Move ${path} later in the header`}
        disabled={disabled.down}
        onClick={() => onMove("down")}
        className="px-xxs text-micro text-muted hover:text-ink disabled:opacity-30"
      >
        ▼
      </button>
    </span>
  );
}

/**
 * Adding a statistic without leaving the row that needed it.
 *
 * Three inputs, where ADR-0029 specified two — see its Corrections, which record
 * this and why it does not reopen the single-text-field option that ADR rejected.
 * Sport comes from the source being added or reviewed, the key is derived from
 * the name and aliases are empty — none of
 * which a reviewer looking at one column of one feed is in a position to answer.
 *
 * **Two of the three are asked about the statistic and one about this ranking,
 * and they are not written to the same place.** Name and Direction create a Stat
 * Dictionary entry, which is shared by every profile in the sport (ADR-0017) —
 * and that sharing is exactly why this form only ever *creates* one. A name
 * colliding with an existing entry is refused with a visible error and the
 * existing entry is never patched, because a Direction chosen while looking at
 * one column would otherwise invert rankings on sources the reviewer cannot see
 * (ADR-0029). Correcting a Direction is the Stat Dictionary screen's job.
 *
 * The Weight is the third input and is written to **both** places: onto the new
 * entry as its `default_weight`, and back to the row to be saved on this
 * Binding. They are different things — the Binding's weight is what ranks this
 * profile, the entry's is only what a *future* Binding starts from — and the one
 * box answers both because when a statistic is being invented they are the same
 * judgement.
 *
 * **It wrote only to the row until 2026-10-07, and that was a trap.** The
 * behaviour was right while the Stat Dictionary had nowhere to keep a weight;
 * once the entry gained one, a box labelled "Weight %" could be filled in here
 * and the statistic would still show an empty Weight in the dictionary for ever.
 * Found in the running portal, not by a test: `test_abbreviation` held 100 on its
 * Binding and null on its entry.
 *
 * ADR-0040 still governs what *ranks*: a weight the ranker reads lives on the
 * Binding, because soccer's dictionary is shared by Wyscout, the six ESPN leagues
 * and MLS NEXT Pro and `goals: 1.6` filed on the entry would re-rank six enabled
 * Schedules. The entry's copy is inert until a new Binding is made from it.
 *
 * A blank Weight here still leaves whatever the row already had alone rather than
 * clearing it.
 *
 * It is asked *here*, beside Direction, because this is where somebody is
 * already deciding how the statistic ranks, and a weight nobody is offered at
 * that moment is a weight that stays blank.
 *
 * **Direction is asked and not defaulted.** `StatEntryIn` defaults it to
 * `higher_is_better`, so the single text field that was asked for would declare
 * every new statistic that way — ranking a quarterback better for throwing
 * more interceptions. There is no error and no failed Run at the end of that:
 * just a published file that looks entirely normal and is ordered backwards.
 *
 * The entry is written immediately rather than held until the profile saves.
 * `createProfile` and `replaceBindings` are separate calls, so holding it does
 * not avoid the orphan it would exist to avoid — it only makes it later.
 */
export function NewStatisticForm({
  sport,
  profileExists = true,
  draft,
  entries,
  onCreated,
  onCancel,
}: {
  sport: string;
  /**
   * Whether the source being reviewed has been saved yet.
   *
   * False on Add Source, where the profile does not exist until the screen is
   * submitted — the same fact that makes a Binding name its Feed by label
   * rather than by id. It reaches `PositionGroupPicker`, which withdraws
   * add-a-group there: a group is made of the labels a source published, and a
   * source that does not exist has published none.
   */
  profileExists?: boolean;
  /** The unsaved source, on Add Source. See `PositionGroupPicker`. */
  draft?: DraftSource;
  /** The dictionary as loaded, of any sport. Filtered here rather than by the
   *  caller, so the duplicate check is right on the screen that loads all of
   *  it as well as the one that loads a single sport's. */
  entries: StatEntry[];
  /**
   * The entry that was created, and what this row's Binding should weigh.
   *
   * Two arguments because they are saved by two different calls: the entry is
   * written here and immediately, the weight travels back to the row and is
   * saved with the Bindings. Null is "nothing declared", which weighs 1.
   */
  onCreated: (statKey: string, weight: number | null) => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  // Empty until chosen. A Direction that starts on a value is a Direction
  // nobody had to think about, which is the whole of what this control is for.
  const [direction, setDirection] = useState<Direction | "">("");
  /**
   * The Weight box as typed, not as a number.
   *
   * Held as text so a half-typed "0." or "1." survives the keystroke that made
   * it — parsing on every change and storing the number would reject "0." and
   * put the cursor behind a value nobody finished typing. Parsed on submit, and
   * blank stays blank: unlike Direction, which cannot be guessed and so is
   * asked, a Weight has a correct default and ADR-0040 keeps "nothing declared"
   * a distinct review state from 1.0 written out.
   */
  const [weight, setWeight] = useState("");
  /**
   * Which Canonical Position Groups this statistic will not rank.
   *
   * **Asked here although it is read by every source in the sport**, which is
   * the opposite of ADR-0029's rule for Direction — and allowed for the same
   * reason the Weight beside it is: this form only ever *creates*. A colliding
   * name is refused with a visible error and no existing entry is ever patched,
   * so a declaration made while looking at one column cannot change what any
   * other profile ranks. Correcting one is the Stat Dictionary screen's job.
   *
   * It is asked at all because this is where somebody is deciding how a
   * statistic ranks, and "goalkeepers are not ranked on this" is the same kind
   * of judgement as the Direction and the Weight already on the row. A
   * declaration only reachable from another screen is one nobody makes.
   */
  const [excluded, setExcluded] = useState<string[]>([]);
  /**
   * Which control is at fault, and what is wrong with it.
   *
   * Attributed to a field rather than kept as a loose sentence, because that is
   * what puts the message under the control and `aria-invalid` on it — the
   * stylesheet drives invalid styling from that attribute precisely so the
   * thing that colours a field is the thing that tells a screen reader about
   * it. A refusal with no field to blame is the server's, and goes to the
   * banner.
   */
  const [problem, setProblem] = useState<{
    field: "name" | "direction" | "weight";
    message: string;
  } | null>(null);

  const key = statKeyFrom(name);

  /**
   * The Canonical Position Groups this sport offers.
   *
   * One query, shared with the Stat Dictionary screen by key, so a review
   * opening this form twice asks the server once.
   */
  const { data: positionGroups } = useQuery({
    queryKey: keys.positionGroups,
    queryFn: api.listPositionGroups,
  });

  /**
   * The entry already holding this key, if there is one.
   *
   * Checked against the loaded list before posting, and named by its
   * **display name** rather than by what was typed: "Passing Yards" and
   * "passing  yards" slugify alike, so quoting his own text back at him would
   * read as nonsense. The 409 stays as the fallback for the race.
   */
  const clash = entries.find((entry) => entry.sport === sport && entry.key === key) ?? null;

  const duplicateMessage = (entry: StatEntry) =>
    `The ${sport} Stat Dictionary already has "${entry.display_name}" under ${entry.key}. ` +
    "Choose it from the dropdown, or give this one a different name.";

  const create = useMutation({
    mutationFn: () => {
      const parsed = parseWeightPercent(weight);
      return api.createStat({
        sport,
        key,
        display_name: name.trim(),
        direction: direction as Direction,
        // **The typed Weight is filed on the entry as well as on the row.**
        //
        // It used to go only to the row, and that was right while the Stat
        // Dictionary had nowhere to put a weight. Once the entry gained a
        // `default_weight` it stopped being right and started being a trap: the
        // box said "Weight %", somebody typed 100, and the Stat Dictionary then
        // showed that statistic with an empty Weight for ever. Reported from the
        // running portal on 2026-10-07 against `test_abbreviation`, whose
        // Binding held 100 while its entry held null.
        //
        // **Safe because this form only ever creates.** ADR-0029's rule is that
        // an existing entry is never patched from a field-mapping row — a
        // colliding name is still refused with a visible error, above and
        // unchanged — so no other profile's default can be altered from here.
        // Writing a default onto an entry that did not exist a moment ago
        // overwrites nobody's decision.
        //
        // Omitted rather than sent as null when the box is blank, so the entry
        // records "nothing declared" instead of a decision nobody made.
        ...(parsed.ok && parsed.weight != null ? { default_weight: parsed.weight } : {}),
        // Empty, deliberately. `_index_dictionary` indexes aliases normalised
        // and `_suggest` matches a path's leaf, so an alias harvested from the
        // field being mapped would apply this row's local decision to every
        // future source in the sport, silently. The Vocabulary refuses this by
        // hand — `interceptions_thrown` is deliberately not aliased to bare
        // `Int`, which means the opposite on a defensive feed — and a control
        // in a table cell cannot make that judgement (ADR-0029).
        //
        // Auto-binding is still available without them: the normalised
        // *display name* is indexed too, so naming a statistic what the source
        // calls it — "QBR" on `passing.QBR` — binds on every future discovery
        // with no alias at all.
        aliases: [],
        // Omitted rather than sent empty where nothing was ticked, so the entry
        // records the same "no exclusions" a caller saying nothing records.
        //
        // Safe on the same terms as the weight above: this form only creates,
        // so a declaration made from a field-mapping row cannot change what any
        // existing profile ranks. Only the groups `positionGroups` offered for
        // this sport can be in here, and the server refuses any other.
        ...(excluded.length > 0 ? { excluded_position_groups: excluded } : {}),
        // The optional defaults are left off so they take `StatEntryIn`'s,
        // which is every Vocabulary entry except games played.
      });
    },
    onSuccess: async (entry) => {
      // The prefix, so the review screen's whole-dictionary query and the add
      // screen's one-sport query both go stale on one call.
      await queryClient.invalidateQueries({ queryKey: keys.stats });
      // The row gets the weight too, because the Binding is what actually ranks
      // — the entry's copy is only the default a *future* Binding starts from.
      //
      // Taken from the entry the server wrote rather than re-parsed from the
      // box, so the two places this number now lives cannot disagree: whatever
      // the server stored is what this row shows.
      onCreated(entry.key, entry.default_weight);
    },
    onError: async (error) => {
      // A 409 means the list this form checked against was behind the
      // dictionary — somebody else added the statistic, or a refresh had not
      // landed. Refetch, so the entry that is actually there can be named:
      // the server's own message can only name the *key*, and the key is
      // neither what was typed nor what the dropdown will call it.
      if (error instanceof ApiError && error.status === 409) {
        await queryClient.invalidateQueries({ queryKey: keys.stats });
      }
    },
  });

  /**
   * Editing retracts the last refusal — this form's and the server's.
   *
   * Without resetting the mutation, a refusal the server made outlives the
   * name it was about: `problem` clears on the keystroke but `create.error`
   * does not, so the banner goes on naming a statistic that is no longer typed
   * in the box above it.
   */
  function edited() {
    setProblem(null);
    if (create.isError) create.reset();
  }

  function submit() {
    if (name.trim() === "") {
      setProblem({
        field: "name",
        message: "Give the statistic a name. It is what the dropdown will call it.",
      });
      return;
    }
    if (key === "") {
      setProblem({
        field: "name",
        message: "That name has no letters or digits in it, so there is no key to file it under.",
      });
      return;
    }
    if (direction === "") {
      setProblem({
        field: "direction",
        message:
          "Say which way this statistic runs. It decides whether a big number ranks a player up or down, and it cannot be guessed from the data.",
      });
      return;
    }
    // After Direction and before the duplicate check, which is the order the
    // controls are in. A form that refused the name for being taken while the
    // Weight beside it was also wrong would hide the second refusal behind the
    // first and need two submits to find out.
    const parsedWeight = parseWeightPercent(weight);
    if (!parsedWeight.ok) {
      setProblem({ field: "weight", message: parsedWeight.message });
      return;
    }
    if (clash) {
      setProblem({ field: "name", message: duplicateMessage(clash) });
      return;
    }
    setProblem(null);
    create.mutate();
  }

  // A refusal the server made reads the same as one this form made, once the
  // refetch above has found the entry the server was talking about. Only a
  // 409 whose entry still cannot be found falls back to the server's wording.
  const conflicted = create.error instanceof ApiError && create.error.status === 409;

  // The name is at fault for a duplicate however the duplicate was found, so a
  // 409 reads the same as this form's own check once the refetch above has
  // found the entry the server was talking about.
  const nameProblem =
    (problem?.field === "name" ? problem.message : null) ??
    (conflicted && clash ? duplicateMessage(clash) : null);
  const directionProblem = problem?.field === "direction" ? problem.message : null;
  const weightProblem = problem?.field === "weight" ? problem.message : null;

  // What is left: a refusal with no control to pin it on. Only a 409 whose
  // entry still cannot be found falls back to the server's own wording.
  const refusal =
    nameProblem || directionProblem || weightProblem
      ? null
      : create.error instanceof ApiError
        ? create.error.message
        : create.error
          ? "Could not add that statistic."
          : null;

  return (
    // A real form, so Enter in the Name box adds the statistic. Every other
    // add-a-statistic form in the portal is one, and the row this sits in has
    // no enclosing form to nest inside.
    <form
      className="flex flex-col gap-sm rounded-lg border border-hairline bg-surface-soft px-md py-sm"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <strong className="text-body-sm-medium">New statistic</strong>

      <div className="grid grid-cols-1 gap-md fold:grid-cols-3">
        <Field label="Name" hint="What the dropdown will call it." error={nameProblem}>
          <TextInputCompact
            value={name}
            autoFocus
            placeholder="Adjusted QBR"
            aria-invalid={nameProblem ? true : undefined}
            onChange={(e) => {
              setName(e.target.value);
              edited();
            }}
          />
        </Field>
        <Field label="Direction" hint="Nothing in a payload reveals this." error={directionProblem}>
          <SelectCompact
            value={direction}
            aria-invalid={directionProblem ? true : undefined}
            onChange={(e) => {
              setDirection(e.target.value as Direction | "");
              edited();
            }}
          >
            <option value="">Which way does it run?</option>
            <option value="higher_is_better">Higher is better</option>
            <option value="lower_is_better">Lower is better</option>
          </SelectCompact>
        </Field>
        {/* Beside Direction because the two are the same decision made twice —
            which way the statistic runs, and how much of the ranking it gets.
            Optional where Direction is not: a blank Direction ranks a statistic
            backwards with no error at the end of it, where a blank Weight
            ranks it at 1 alongside everything else nobody weighted. */}
        <Field
          label="Weight %"
          hint="Share of the ranking. Blank counts as 1."
          error={weightProblem}
        >
          <TextInputCompact
            value={weight}
            inputMode="decimal"
            placeholder="Optional"
            aria-invalid={weightProblem ? true : undefined}
            onChange={(e) => {
              setWeight(e.target.value);
              edited();
            }}
          />
        </Field>
      </div>

      {/* On its own line under the three, rather than as a fourth column.
          Direction, Weight and the name are all one short control each; this one
          holds a list and its hint is a sentence, and a four-column grid at the
          width of a table row would truncate both.

          **It is read by every source in the sport, unlike the Weight above.**
          That is safe here only because this form never patches an existing
          entry — see the state's own comment — and the hint says what it does so
          nobody has to infer it from where the control happens to be. */}
      {/* `FieldSet` and not `Field`, which is a `<label>`. The picker's shut
          control is a `<button>` and its panel holds a `<label>` per checkbox,
          so wrapping it in one would nest labels — invalid, and a click
          forwarded from the outer label to a labelable `<button>` is a panel
          that opens from its own caption. `FieldSet` is the `<div>` the Stat
          Dictionary's own copy of this control already uses. */}
      <FieldSet
        label="Positions Groups Excluded"
        // Behind the label, like the Stat Dictionary's add form and for the
        // same reason: this form only ever *creates*, so the declaration warns
        // of nothing — a statistic being invented has no Bindings for it to
        // re-rank — and a row in a six-hundred-field table cannot afford a
        // permanent paragraph per control.
        tip="Optional. These Canonical Position Groups are not ranked on this statistic, in every source in this sport. A group is the publisher's own labels collected under one name, so adding one is adding a mapping."
      >
        <PositionGroupPicker
          compact
          sport={sport}
          profileExists={profileExists}
          draft={draft}
          options={optionsFor(positionGroups, sport, excluded)}
          labels={labelsFor(positionGroups, sport)}
          value={excluded}
          onChange={(groups) => {
            setExcluded(groups);
            edited();
          }}
        />
      </FieldSet>
      {exclusionHint(excluded) && <Muted>{exclusionHint(excluded)}</Muted>}

      <div className="flex flex-wrap items-center justify-between gap-sm">
        <Muted>
          {sport}
          {key ? ` · saves as ${key}` : " · the key is taken from the name"}
        </Muted>
        <div className="flex items-center gap-xs">
          <Button variant="primary" size="sm" type="submit" disabled={create.isPending}>
            {create.isPending ? "Adding…" : "Add"}
          </Button>
          <Button size="sm" disabled={create.isPending} onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>

      {/* Whatever no single control can be blamed for. The form stays open and
          keeps what was typed either way: a refused name is a name that needs
          editing, and clearing it would mean typing it again to find out what
          was wrong with it. */}
      {refusal && (
        <Banner tone="danger" title="That statistic was not added">
          {refusal}
        </Banner>
      )}
    </form>
  );
}

/**
 * The form on its own table row, under the field it was opened from.
 *
 * A row rather than something inside the Statistic cell, because the cell is
 * one of five or six in a scrolling table and a form in it would be a form in a
 * column. The `colSpan` differs between the two screens, which is the only
 * thing either has to say about it.
 */
export function NewStatisticRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan}>{children}</td>
    </tr>
  );
}

/**
 * Why this row cannot be saved, said under the row.
 *
 * The whole of what a refusal used to be was one sentence in a banner beside
 * the Save button, naming a path, on a table that can run to six hundred rows.
 * Finding the row it meant was the reader's problem — so the message moved to
 * the row, and the banner now counts what is marked instead of describing one
 * of them.
 *
 * A row of its own rather than something in the Statistic cell, for the reason
 * `NewStatisticRow` gives: the cell is one of six in a scrolling table, and a
 * sentence in it would be a sentence in a column two words wide. `role="alert"`
 * so that a problem appearing under the row somebody is editing is announced
 * rather than merely drawn.
 *
 * Both screens pass their own `colSpan`, which is the only thing either has to
 * say about it.
 *
 * Named for the note rather than the row: `ProblemRow` in `binding-problems.ts`
 * is the row these notes are about, and one name for both meant both screens
 * imported this under an alias.
 */
export function ProblemNote({ colSpan, message }: { colSpan: number; message: string }) {
  return (
    <tr data-problem-note="">
      <td colSpan={colSpan} className="pt-0">
        <span role="alert" className="flex items-start gap-xs text-caption text-danger-ink">
          <AlertIcon size={14} className="mt-[1px] shrink-0" />
          {message}
        </span>
      </td>
    </tr>
  );
}
